import { useAssessmentDefaultValues } from "app/features/reporting/hooks/useAssessmentDefaultValues";
import { useAssessmentPagination } from "app/features/reporting/hooks/useAssessmentPagination";
import { useAssessmentSaving } from "app/features/reporting/hooks/useAssessmentSaving";
import { QuestionTypeEnum } from "app/services/api/types/forms/enums/QuestionTypeEnum";
import { AssessmentModel } from "app/services/api/types/forms/models/AssessmentModel";
import { AssessmentSectionModel } from "app/services/api/types/forms/models/AssessmentSectionModel";
import { SubmittedFormModel } from "app/services/api/types/forms/models/SubmittedFormModel";
import { createContext, useContext, useEffect } from "react";
import { Control, useForm, UseFormHandleSubmit } from "react-hook-form";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { ThrelsFileType } from "ui/types/ThrelsFileType";

export interface AssessmentSubmissionFormModel {
  [question_id: string]: {
    value: string[];
    free_text?: string;
    files?: ThrelsFileType[];
    question_type: QuestionTypeEnum;
  };
}

interface Props {
  children: React.ReactNode;
  assessmentModel: AssessmentModel;
  entriesAreVersioned?: boolean;
  entryData?: SubmittedFormModel;
  onSubmit?: () => void;
}

interface AssessmentFormContextValue {
  assessmentModel: AssessmentModel;
  sections: AssessmentSectionModel[];
  currentPageIndex: number;
  totalPages: number;
  setCurrentPageIndex: (index: number) => void;
  control: Control<AssessmentSubmissionFormModel>;
  isSubmitting: boolean;
  isDirty: boolean;
  handleSubmit: UseFormHandleSubmit<
    AssessmentSubmissionFormModel,
    AssessmentSubmissionFormModel
  >;
  submitForm: (data: AssessmentSubmissionFormModel) => void;
  saveAsDraftForm: (data: AssessmentSubmissionFormModel) => void;
}

const AssessmentFormContext = createContext<AssessmentFormContextValue>(
  {} as AssessmentFormContextValue,
);

export function AssessmentFormProvider({
  children,
  assessmentModel,
  entriesAreVersioned,
  entryData,
  onSubmit,
}: Props) {
  const { showNewToast, showErrorToast, showOfflineToast } = useDisplayToast();

  const {
    sections: currentSections,
    currentPageIndex,
    setCurrentPageIndex,
    totalPages,
  } = useAssessmentPagination({ form: assessmentModel });

  const { save: saveEntry } = useAssessmentSaving({
    assessmentModel,
    entriesAreVersioned,
    entryData,
  });

  const { generateDefaultValues } = useAssessmentDefaultValues();

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm<AssessmentSubmissionFormModel>({
    defaultValues: generateDefaultValues(assessmentModel, entryData),
  });

  useEffect(() => {
    if (entryData) {
      const data = generateDefaultValues(assessmentModel, entryData);
      reset(data);
    }
  }, [entryData]);

  const saveAsDraftForm = async (data: AssessmentSubmissionFormModel) => {
    await save(data, true);
  };

  const submitForm = async (data: AssessmentSubmissionFormModel) => {
    await save(data, false);
  };

  const save = async (
    data: AssessmentSubmissionFormModel,
    isDraft: boolean,
  ) => {
    try {
      await saveEntry({ data, isDraft });
      reset();

      showNewToast({
        title: "Form submitted",
        message: "Successfully submitted form",
        type: "success",
      });

      if (onSubmit) onSubmit();
    } catch (e) {
      showOfflineToast(
        {
          title: "Form saved offline",
        },
        () =>
          showErrorToast(e as Parameters<typeof showErrorToast>[0], {
            title: "Form not submitted",
            statusOverride: {
              // 422 is deliberately not overridden: the backend sends a
              // message naming the question, which is what the user needs.
              500: "Something went wrong submitting the form. Please try again.",
            },
          }),
      );
    }
  };

  return (
    <AssessmentFormContext.Provider
      value={{
        assessmentModel,
        sections: currentSections,
        currentPageIndex,
        totalPages,
        setCurrentPageIndex,
        control,
        isSubmitting,
        isDirty,
        handleSubmit,
        submitForm,
        saveAsDraftForm,
      }}
    >
      {children}
    </AssessmentFormContext.Provider>
  );
}

export function useAssessmentFormContext() {
  const context = useContext(AssessmentFormContext);
  if (!context) {
    throw new Error(
      "AssessmentProviderContext must be used within a AssessmentProvider",
    );
  }
  return context;
}
